import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import { useLlmModels } from "@/hooks/useLlmModels";
import { NewMedicalTextDialog } from "@/test-data/NewMedicalTextDialog";
import dayjs from "dayjs";
import { AlertCircle, History, Settings } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import type { RunResultWithStages, RunStageGpSummary } from "../useRunResultWithStages";
import { DiagnosisCodeChip } from "./DiagnosisCodeChip";
import { getAllCoMorbidities } from "./normaliseCodesForDisplay";
import DebugInfoJsonViewer from "./DebugInfoJsonViewer";
import useRegressions from "./useRegressions";

interface RunGpSummaryStageDetailsProps {
  run: RunResultWithStages;
  stageKey: string;
  stageResult: RunStageGpSummary | null;
}

const RunGpSummaryStageDetails = ({ run, stageKey, stageResult }: RunGpSummaryStageDetailsProps) => {
  const navigate = useNavigate();
  const [medicalTextDialogOpen, setMedicalTextDialogOpen] = useState(false);

  const { data: llmModels } = useLlmModels();

  const {
    lastSuccessQuery: { data: lastSuccessfulRun },
    regressionsQuery: { data: regressions },
  } = useRegressions({
    runId: run.id,
    runEndTime: run.run_end_time,
  });

  if (!stageResult?.result) return null;

  const coMorbidities = getAllCoMorbidities({
    expectedCoMorbidities: stageResult.test_data.expected_co_morbidities.map((diag) => ({
      ...diag.icd10_codes,
      id: diag.code_id,
      isOptional: !!diag.is_optional,
      type: "ICD-10",
    })),
    runCoMorbidities: stageResult.result.diagnoses,
  });

  return (
    <>
      <NewMedicalTextDialog
        open={medicalTextDialogOpen}
        onClose={() => setMedicalTextDialogOpen(false)}
        medicalTextId={stageResult.test_data_id}
      />
      <div className="h-[calc(100vh-4rem)] flex">
        {/* Left column */}
        <div className="w-full max-w-md border-r h-full overflow-y-auto p-2">
          <Card className="rounded-md border bg-muted/30 mb-6">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="inline-block px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                    GP Summary
                  </span>
                  {stageResult.test_data.label ? (
                    <span className="text-muted-foreground text-sm">({stageResult.test_data.label})</span>
                  ) : null}
                  <span className="text-muted-foreground text-sm">({stageResult.test_data.sequence_number})</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setMedicalTextDialogOpen(true)}
                  title="View/Edit Medical Text Example"
                  aria-label="View/Edit Medical Text Example"
                >
                  <Settings className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <MarkdownRenderer
                content={stageResult.test_data.text}
                className="bg-background rounded p-2 overflow-x-auto max-w-full"
              />
            </CardContent>
          </Card>
        </div>
        {/* Right column */}
        <div className="flex-1 h-full overflow-auto p-6 space-y-6 max-w-full">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-4">
                Run Details
                {run.run_end_time && (
                  <span className="text-base font-normal text-muted-foreground bg-muted px-3 py-1 rounded-full ml-2">
                    {dayjs(run.run_start_time).tz().format("DD/MM HH:mm")}
                  </span>
                )}
                {regressions && regressions.length > 0 && (
                  <span className="flex items-center gap-1 text-base font-normal bg-red-100 text-red-800 px-3 py-1 rounded-full ml-2">
                    <AlertCircle className="h-4 w-4 mr-1 text-red-600" />
                    Regression
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Go to last successful run"
                      aria-label="Go to last successful run"
                      disabled={!lastSuccessfulRun}
                      onClick={() => {
                        if (lastSuccessfulRun) {
                          navigate(`/test-data/runs/${lastSuccessfulRun.id}`);
                        }
                      }}
                    >
                      <History className="h-5 w-5" />
                    </Button>
                  </span>
                )}
                <span className="text-base font-normal text-muted-foreground bg-muted px-3 py-1 rounded-full ml-2">
                  {llmModels
                    ? llmModels.find((model) => model.modelId === run.llm_model_id)?.name || "Unknown"
                    : "Loading model..."}
                </span>
              </h1>
              <p className="text-muted-foreground">Details for run {run.id}</p>
              {run.error_message && (
                <div className="text-red-500 mt-2">
                  <p className="font-medium">There was a top level error preventing classification:</p>
                  <pre className="text-sm bg-muted p-2 rounded mt-1 whitespace-pre-wrap overflow-x-auto max-w-full break-words">
                    {run.error_message}
                  </pre>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-md border p-4 space-y-4 w-full max-w-full">
              <h2 className="text-lg font-medium mb-2">Co-Morbidities</h2>
              <div className="flex flex-col gap-2">
                {coMorbidities.map((diagnosis, _index) => (
                  <DiagnosisCodeChip
                    key={diagnosis.id || diagnosis.code}
                    codeId={diagnosis.id}
                    code={diagnosis.code}
                    description={diagnosis.description}
                    isExpected={diagnosis.isExpected}
                    isPresent={diagnosis.isPresent}
                    isPositionCorrect={true}
                    isOptional={diagnosis.isOptional}
                  />
                ))}
                {coMorbidities.length === 0 && <div className="text-muted-foreground">No co-morbidities found</div>}
              </div>
            </div>

            {stageResult.result.debugInfo && (
              <div className="rounded-md border p-4 w-full max-w-full">
                <h2 className="text-lg font-medium mb-2">Debug Info</h2>
                <DebugInfoJsonViewer debugInfo={stageResult.result.debugInfo} />
              </div>
            )}

            {stageResult.result.errorMessage && (
              <div className="rounded-md border-2 border-red-500 bg-red-50 p-4">
                <div className="font-medium text-red-800 mb-1">Error</div>
                <div className="text-sm text-red-600 whitespace-pre-wrap break-words">
                  {stageResult.result.errorMessage}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RunGpSummaryStageDetails;
